import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import API from '../../services/api';
import { useNotificationsContext } from '../../contexts/NotificationsContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useTranslation } from '../../hooks/useTranslation';
import NotificationBanner from '../UI/NotificationBanner';
import Toast from '../UI/Toast';
import {
  Home,
  Baby,
  User,
  LogOut,
  Crown,
  Menu,
  X,
  Shield,
  Package,
  MessageCircle,
  ChevronDown
} from 'lucide-react';

import logoTitaton from '../../assets/images/logo-titaton.svg';
import './layout.css';

const PhenixNavIcon = ({ size = 18 }) => (
  <svg viewBox="0 0 32 32" fill="none" width={size} height={size} style={{ color: 'currentColor' }}>
    <path d="M16 28c-1.5-2-3-5-3-8 0-4 2-7 5-9l-1-3c-4 1-7 5-7 10 0 4 2.5 7.5 6 10z" fill="currentColor" opacity="0.9"/>
    <path d="M8 12c2-3 5-5 9-5l-1-3C11 5 7 8 5 13l3-1z" fill="currentColor" opacity="0.7"/>
    <path d="M24 12c-2-3-4-5-7-5l1-3c4 1 7 4 9 9l-3-1z" fill="currentColor" opacity="0.7"/>
    <circle cx="16" cy="6" r="3" fill="currentColor"/>
    <path d="M14 4l-2-3M16 3l0-3M18 4l2-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M13 24c-1 2-2 4-1 6M16 25c0 2 0 5 1 7M19 24c1 2 2 4 1 6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.6"/>
  </svg>
);

const Layout = ({ children }) => {
  const { user, logout, isPremium, isParent, isAdmin } = useAuth();
  const {
    peutPartagerFeed,
    loading: permissionsLoading
  } = usePermissions();
  const { totalUnread } = useNotificationsContext();
  const { t, getRoleText } = useTranslation();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleResendVerification = useCallback(async () => {
    setResendingEmail(true);
    try {
      await API.auth.resendVerification();
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch {
      // silently fail
    } finally {
      setResendingEmail(false);
    }
  }, []);

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const confirmLogout = () => {
    setShowLogoutModal(false);
    logout();
  };

  // Close menus on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setShowUserMenu(false);
  }, [location.pathname]);

  // Close user menu on outside click
  useEffect(() => {
    if (!showUserMenu) return;
    const handleClick = () => setShowUserMenu(false);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [showUserMenu]);

  const getNavigationLinks = () => {
    if (isAdmin) {
      return [
        { to: '/admin', icon: <Shield size={18} />, label: 'Administration' }
      ];
    }

    const links = [
      {
        to: '/dashboard',
        icon: <Home size={18} />,
        label: 'Accueil',
        show: true
      },
      {
        to: '/coffre',
        icon: <Package size={18} />,
        label: 'Coffre',
        show: true
      },
      {
        to: '/phenix',
        icon: <PhenixNavIcon size={18} />,
        label: 'Phenix',
        show: true
      },
      {
        to: '/messages',
        icon: <MessageCircle size={18} />,
        label: 'Discussions',
        show: true,
        badge: totalUnread > 0 ? (totalUnread > 99 ? '99+' : totalUnread) : null
      },
      {
        to: '/enfants',
        icon: <Baby size={18} />,
        label: isParent ? 'Mes enfants' : 'Enfants suivis',
        show: true
      },
      // Invitations integrees dans /enfants — lien retire du menu
    ];

    return links.filter(link => link.show);
  };

  const navigationLinks = getNavigationLinks();

  if (permissionsLoading) {
    return (
      <div className="layout-loading">
        <div className="spinner" />
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="layout-titaton">
      <a href="#main-content" className="skip-link">Aller au contenu principal</a>

      {/* Top navigation bar */}
      <header className="topbar" role="banner">
        <div className="topbar-inner">
          {/* Brand */}
          <Link to={isAdmin ? '/admin' : '/dashboard'} className="topbar-brand">
            <img src={logoTitaton} alt="Titaton" className="topbar-logo" />
            <span className="topbar-wordmark">Titaton</span>
          </Link>

          {/* Desktop nav */}
          <nav className="topbar-nav" aria-label="Navigation principale">
            {navigationLinks.map((link, i) => (
              <Link
                key={i}
                to={link.to}
                className={`topbar-link ${isActive(link.to) ? 'active' : ''}`}
                aria-current={isActive(link.to) ? 'page' : undefined}
              >
                <span className="nav-icon-wrap">
                  {link.icon}
                  {link.badge && (
                    <span className="nav-badge">{link.badge}</span>
                  )}
                </span>
                <span>{link.label}</span>
              </Link>
            ))}
          </nav>

          {/* Right side: premium + user */}
          <div className="topbar-right">
            {!isPremium && isParent && (
              <Link to="/premium" className="topbar-premium">
                <Crown size={14} />
                <span>Premium</span>
              </Link>
            )}

            <div className="topbar-user-wrap" onClick={(e) => { e.stopPropagation(); setShowUserMenu(!showUserMenu); }}>
              <button className="topbar-user" aria-label="Menu utilisateur" aria-expanded={showUserMenu}>
                <div className="topbar-avatar">
                  {user?.prenom?.charAt(0)}
                </div>
                {isPremium && <Crown size={12} className="topbar-crown" />}
                <ChevronDown size={14} className={`topbar-chevron ${showUserMenu ? 'open' : ''}`} />
              </button>

              {showUserMenu && (
                <div className="user-dropdown">
                  <div className="user-dropdown-header">
                    <div className="user-dropdown-name">{user?.prenom} {user?.nom}</div>
                    <div className="user-dropdown-role">{getRoleText(user?.role, true)}</div>
                  </div>
                  <div className="user-dropdown-divider" />
                  <Link to="/profil" className="user-dropdown-item">
                    <User size={16} />
                    <span>Mon profil</span>
                  </Link>
                  {!isPremium && isParent && (
                    <Link to="/premium" className="user-dropdown-item premium">
                      <Crown size={16} />
                      <span>Passer Premium</span>
                    </Link>
                  )}
                  <div className="user-dropdown-divider" />
                  <button className="user-dropdown-item logout" onClick={() => setShowLogoutModal(true)}>
                    <LogOut size={16} />
                    <span>Se déconnecter</span>
                  </button>
                </div>
              )}
            </div>

            {/* Mobile toggle */}
            <button
              className="topbar-mobile-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <nav className="mobile-menu" aria-label="Navigation mobile">
            {navigationLinks.map((link, i) => (
              <Link
                key={i}
                to={link.to}
                className={`mobile-menu-link ${isActive(link.to) ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="nav-icon-wrap">
                  {link.icon}
                  {link.badge && (
                    <span className="nav-badge">{link.badge}</span>
                  )}
                </span>
                <span>{link.label}</span>
              </Link>
            ))}
            <div className="mobile-menu-divider" />
            <Link to="/profil" className="mobile-menu-link" onClick={() => setIsMobileMenuOpen(false)}>
              <User size={18} />
              <span>Mon profil</span>
            </Link>
            <button className="mobile-menu-link logout" onClick={() => { setIsMobileMenuOpen(false); setShowLogoutModal(true); }}>
              <LogOut size={18} />
              <span>Se déconnecter</span>
            </button>
          </nav>
        )}
      </header>

      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div className="mobile-overlay" onClick={() => setIsMobileMenuOpen(false)} aria-hidden="true" />
      )}

      {/* Main content */}
      <main id="main-content" className="main-content">
        {/* Email verification banner */}
        {user && user.emailVerifie === false && user.provider === 'local' && (
          <div className="email-verify-banner">
            <div className="email-verify-content">
              <span className="email-verify-icon">✉️</span>
              <div className="email-verify-text">
                <strong>Vérifiez votre adresse email</strong>
                <span>Un email a été envoyé à {user.email}. Vérifiez pour sécuriser votre compte.</span>
              </div>
              <button
                className="email-verify-btn"
                onClick={handleResendVerification}
                disabled={resendingEmail || resendSuccess}
              >
                {resendSuccess ? '✓ Email envoyé' : resendingEmail ? '...' : 'Renvoyer'}
              </button>
            </div>
          </div>
        )}

        {/* Account deletion banner */}
        {user && user.suppressionDemandeeA && (
          <div className="email-verify-banner deletion-banner">
            <div className="email-verify-content">
              <span className="email-verify-icon">⚠️</span>
              <div className="email-verify-text">
                <strong>Compte en cours de suppression</strong>
                <span>
                  {user.suppressionPrevueA
                    ? `Vos données seront anonymisées le ${new Date(user.suppressionPrevueA).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}.`
                    : 'Vos données seront anonymisées dans 30 jours.'}
                </span>
              </div>
              <Link to="/suppression-donnees" className="email-verify-btn">
                Annuler
              </Link>
            </div>
          </div>
        )}

        {/* Fondu doux à chaque changement de page (opacité seule → n'affecte pas
            le positionnement fixed des rails/modales). Keyé sur le chemin pour rejouer. */}
        <div key={location.pathname} className="page-transition">
          {children}
        </div>
      </main>

      {/* Legal footer */}
      <footer className="legal-footer" aria-label="Liens legaux">
        <nav className="legal-footer-nav">
          <Link to="/cgv">CGV</Link>
          <span className="legal-footer-sep" aria-hidden="true">·</span>
          <Link to="/confidentialite">Confidentialité</Link>
          <span className="legal-footer-sep" aria-hidden="true">·</span>
          <Link to="/remboursement">Remboursement</Link>
          <span className="legal-footer-sep" aria-hidden="true">·</span>
          <Link to="/mentions-legales">Mentions legales</Link>
          <span className="legal-footer-sep" aria-hidden="true">·</span>
          <Link to="/suppression-donnees">Suppression des données</Link>
        </nav>
      </footer>

      <NotificationBanner />
      <Toast />

      {/* Logout modal */}
      {showLogoutModal && (
        <div className="logout-modal-overlay" onClick={() => setShowLogoutModal(false)} role="dialog" aria-modal="true">
          <div className="logout-modal" onClick={(e) => e.stopPropagation()}>
            <div className="logout-modal-icon">
              <LogOut size={28} />
            </div>
            <h3 className="logout-modal-title">Se déconnecter</h3>
            <p className="logout-modal-text">Êtes-vous sûr de vouloir vous déconnecter ?</p>
            <div className="logout-modal-actions">
              <button className="logout-modal-btn logout-modal-cancel" onClick={() => setShowLogoutModal(false)}>
                Annuler
              </button>
              <button className="logout-modal-btn logout-modal-confirm" onClick={confirmLogout}>
                Se déconnecter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
